<template>
  <div class="py-2 px-2 container-fluid modern-scrollbar" v-if="!profile || inProgress">
    <div class="profile-grid">
      <div class="card area-main">
        <div class="card-body">
          <div class="d-flex justify-content-between mb-4">
            <NSkeleton text style="width: 40%; height: 1.75rem;" />
            <NSkeleton text style="width: 6rem; height: 2rem;" />
          </div>
          <NSkeleton text :repeat="4" />
          <NSkeleton text style="width: 75%" />
          <div class="mt-3">
            <NSkeleton text :repeat="3" />
            <NSkeleton text style="width: 55%" />
          </div>
        </div>
      </div>
      <div class="card area-sidebar">
        <div class="card-body">
          <div class="mb-4">
            <NSkeleton text style="width: 50%; height: 1.25rem;" class="mb-2" />
            <NSkeleton text style="width: 80%" />
            <NSkeleton text style="width: 65%" />
          </div>
          <div class="mb-4">
            <NSkeleton text style="width: 45%; height: 1.25rem;" class="mb-2" />
            <NSkeleton text style="width: 90%" />
            <NSkeleton text style="width: 70%" />
          </div>
        </div>
      </div>
      <div class="card area-map">
        <div class="card-body px-0 pb-1">
          <NSkeleton style="width: 100%; height: 400px;" />
          <div class="m-2 mx-3">
            <NSkeleton text style="width: 50%" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="py-2 px-2 container-fluid modern-scrollbar" v-else>
    <div class="profile-grid">

      <!-- Main Info Card -->
      <div class="card area-main">
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <h3 class="mb-4 proxima-blue-text">{{ profile.countryName }}</h3>
            <div>
              <NButton type="info" @click="addNote" color="#213ce5"
                       style="background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent;  border-radius: 3px !important;"
                       @mouseenter="e => e.currentTarget.style.border = '1px solid #00aeef'"
                       @mouseleave="e => e.currentTarget.style.border = '1px solid transparent'">
                {{ has_notes("COUNTRY", route) ? $t("CountryProfile.viewNotesButton") : $t("CountryProfile.addNoteButton") }}
              </NButton>
            </div>
          </div>
          <InfoText
              class="mb-3"
              v-if="profile.geology"
              :title="$t('CountryProfile.geologyHeader')"
              :contents="profile.geology"
          />
          <InfoText
              class="mb-3"
              v-if="profile.production"
              :title="$t('CountryProfile.productionHeader')"
              :contents="profile.production"
          />
          <InfoText
              class="mb-3"
              v-if="profile.ppd"
              :title="$t('CountryProfile.ppdHeader')"
              :contents="profile.ppd"
          />
          <InfoText
              class="mb-3"
              v-if="profile.other"
              :title="$t('CountryProfile.otherHeader')"
              :contents="profile.other"
          />
        </div>
      </div>

      <!-- Sidebar Card -->
      <div class="card area-sidebar">
        <div class="card-body">
          <div class="mb-4" v-if="Object.keys(assetsInCountry).length">
            <h5 class="proxima-blue-text">
              {{ $t("CountryProfile.assetsInCountryHeader", { country: profile.countryName }) }}
            </h5>
            <div class="text-sm" v-for="(value, key) of assetsInCountry" :key="key">
              {{ key }}: {{ value.length }} Operating
            </div>
          </div>
          <div class="mb-4" v-if="companyInCountries.length">
            <h5 class="proxima-blue-text">{{ $t("CountryProfile.listOfCompaniesHeader") }}</h5>
            <div
                class="text-sm"
                :class="{ 'text-bold': companyProfilesInCountries.includes(nth.companyId) }"
                v-for="nth of companyInCountries"
                :key="nth.uuid"
                @click="goToCompanyProfile(nth)"
                style="cursor: pointer"
            >
              {{ nth.companyName }}
            </div>
          </div>
        </div>
      </div>

      <!-- Map Card -->
      <div class="card area-map">
        <div class="card-body px-0 pb-1">
          <iframe
              id="gmap_canvas"
              width="100%"
              height="400"
              :src="`https://maps.google.com/maps?q=${profile.countryName}&t=k&z=5&ie=UTF8&iwloc=&output=embed`"
              frameborder="0"
              scrolling="no"
              marginheight="0"
              marginwidth="0"
          ></iframe>
          <div class="m-2 mx-3">{{ profile.countryName }}</div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ROUTES } from "../../routes/names";
import { NSkeleton, NButton } from "naive-ui";
import {
  MOUNTED_COUNTRY_PROFILE as profile,
  getCountryProfile,
  inProgress,
} from "../../composables/profiles/country";
import InfoText from "../../components/dumb/ProfileInfoText";
import { PUBLISHED_PROFILES } from "../../composables/profiles/list";
import { NOTES_WINDOW, SHOW_ALL_NOTES, has_notes } from "../../composables/notes/list";
import { getCompanyProfile, MOUNTED_COMPANY_PROFILE } from "../../composables/profiles/company";
import { sortBy, uniqBy } from "lodash";
import { logUserAction, USER_ACTIONS } from "@/composables/userLogs";

const route = useRoute();
const router = useRouter();

onMounted(async () => {
  const uuid = route.params?.id;
  const element = route.query?.element;
  if (!uuid || !element) router.push({ name: ROUTES.PROXIMA_PROFILES.name });
  if (!profile.value) await getCountryProfile({ element, uuid });

  // Track country profile access
  const countryName = profile.value?.countryName || profile.value?.country || uuid;
  logUserAction(
    USER_ACTIONS.VIEW_COUNTRY_PROFILE,
    `Viewed Country Profile: "${countryName}" (Commodity: "${element || 'N/A'}")`,
    { endpoint: `/country/${uuid}` }
  );
});

onBeforeUnmount(() => { profile.value = null; });

const addNote = () => {
  NOTES_WINDOW.value = true;
  SHOW_ALL_NOTES.value = false;
};

const assetsInCountry = computed(() => {
  const assets = (PUBLISHED_PROFILES.value?.assetProfiles || []).filter(
      (nth) => nth.countryCode === profile.value?.countryCode
  );
  const types = {};
  for (const nth of assets) {
    if (nth.status !== "Operating") continue;
    if (nth.type in types) types[nth.type].push(nth.status);
    else types[nth.type] = [nth.status];
  }
  return types;
});

const companyProfilesInCountries = computed(() =>
    (PUBLISHED_PROFILES.value?.companyProfiles || []).map((nth) => nth.companyId)
);

const companyInCountries = computed(() => {
  const opts = (PUBLISHED_PROFILES.value?.assetProfiles || [])
      .filter((nth) => nth.countryCode === profile.value?.countryCode)
      .map((nth) => nth);
  const sorted = sortBy(opts, "companyName");
  return uniqBy(sorted, "companyId");
});

const goToCompanyProfile = async (nth) => {
  if (!companyProfilesInCountries.value?.includes(nth.companyId)) {
    window.$message.info("No profile found");
    return;
  }
  const element = route.query.element;
  await getCompanyProfile({ element, uuid: null, id: nth.companyId });
  nextTick(() => {
    if (MOUNTED_COMPANY_PROFILE.value?.uuid) {
      router.push({
        name: ROUTES.COMPANY_PROFILE.name,
        params: { id: MOUNTED_COMPANY_PROFILE.value?.uuid },
        query: { element },
      });
    } else window.$message.info("No profile found");
  });
};
</script>

<style lang="scss" scoped>
* { border-radius: 0 !important; }

.profile-grid {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1fr;
  grid-template-areas:
    "main"
    "sidebar"
    "map";

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "main sidebar"
      "map  map";
  }

  @media (min-width: 992px) {
    grid-template-columns: 2fr 1fr;
    grid-template-areas:
      "main sidebar"
      "map  map";
  }
}

.area-main    { grid-area: main; }
.area-sidebar { grid-area: sidebar; }
.area-map     { grid-area: map; }
</style>